import React from 'react';
import {FlexRow, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {Column} from '../Elements.styled';
import {
	arePhaseTasksComplete,
	derivePhaseSignoffStatus,
	PHASE_SIGNOFF_STATUS_LABEL_ID,
	PhaseSignoffStatus,
	resolveSignoffApproverLabel,
} from '../../../../../util/phaseSignoffStatus';

// Visual weight for each state: SUCCESS for the terminal signed-off row,
// INFORMATION for states where the firm or its client has an action to take,
// NEUTRAL for the passive states. Pill supplies its own accessible contrast in
// both light and dark themes so the label stays legible.
const SIGNOFF_STATUS_PILL_VARIANT = {
	[PhaseSignoffStatus.NOT_CONFIGURED]: PillVariant.NEUTRAL,
	[PhaseSignoffStatus.REQUIRED_BUT_INCOMPLETE]: PillVariant.NEUTRAL,
	[PhaseSignoffStatus.READY_TO_SEND]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.AWAITING_CLIENT]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.CHANGES_REQUESTED]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.SIGNED_OFF]: PillVariant.SUCCESS,
};

/**
 * The sign-off lock moment in the viewer's locale. Returns null for a missing or
 * unparseable timestamp, so the badge degrades to the plain state label rather
 * than printing "Invalid date".
 */
const formatLockedDate = (isoString, intl) => {
	if (!isoString) return null;
	const parsed = new Date(isoString);
	if (Number.isNaN(parsed.getTime())) return null;
	return intl.formatDate(parsed, {year: 'numeric', month: 'short', day: 'numeric'});
};

/**
 * The Sign-off Status cell of a phase header row: a state pill for one of the six
 * client sign-off states, plus — once the phase is locked — when it was signed off
 * and by whom.
 *
 * Both halves of that detail degrade independently, and neither is asserted when it
 * cannot be read. The lock timestamp has no writer yet, and an approver who has lost
 * workspace eligibility drops out of `portalEligibleApprovers`, so the cell composes
 * whichever of the two it actually has and falls back to the bare "Signed off" pill
 * when it has neither.
 *
 * `phase` may be null (a header row with no phase), in which case the cell reports
 * Not configured rather than rendering blank.
 */
export const PhaseSignoffStatusColumn = ({phase, rolledUpValues, portalEligibleApprovers, width, align, intl}) => {
	const portalConfig = phase && phase.portalConfig ? phase.portalConfig : null;
	const status = derivePhaseSignoffStatus({
		requiresClientSignoff: portalConfig ? portalConfig.requiresClientSignoff : false,
		signoffLocked: portalConfig ? portalConfig.signoffLocked : false,
		tasksComplete: arePhaseTasksComplete(rolledUpValues),
	});
	const label = intl.formatMessage({id: PHASE_SIGNOFF_STATUS_LABEL_ID[status]});

	const isSignedOff = status === PhaseSignoffStatus.SIGNED_OFF;
	const lockedDate = isSignedOff && portalConfig ? formatLockedDate(portalConfig.signoffLockedAt, intl) : null;
	const approverName = isSignedOff
		? resolveSignoffApproverLabel(portalConfig ? portalConfig.signoffApproverPortalUserId : null, portalEligibleApprovers)
		: null;

	let signedOffDetail = null;
	if (lockedDate && approverName) {
		signedOffDetail = intl.formatMessage(
			{id: 'client_portal.signoff_state.signed_off_detail_date_name'},
			{date: lockedDate, name: approverName}
		);
	} else if (lockedDate) {
		signedOffDetail = lockedDate;
	} else if (approverName) {
		signedOffDetail = intl.formatMessage({id: 'client_portal.signoff_state.signed_off_detail_name'}, {name: approverName});
	}

	return (
		<Column width={width} align={align}>
			<FlexRow data-cy="phase-signoff-status">
				<Pill variant={SIGNOFF_STATUS_PILL_VARIANT[status]}>{label}</Pill>
				{signedOffDetail && (
					<Text color={'auxiliary'} ellipsis tooltipOnEllipsis data-cy="phase-signoff-detail">
						{signedOffDetail}
					</Text>
				)}
			</FlexRow>
		</Column>
	);
};
